// T-170 Fase 1d — data layer client per la coda import documenti.
// upload pagine su staging, enqueue (RPC idempotente), poll job. Zod-parse al
// boundary (DocumentJobRowSchema) come da invariante "Zod a ogni boundary".

import { getSupabase } from '../supabase.js';
import {
  DocumentJobRowSchema,
  type DocumentJobRow,
} from '@trackeat/shared';

const STAGING_BUCKET = 'fiscal-photo-staging';

function b64ToBytes(b64: string): Uint8Array {
  const clean = b64.includes(',') ? b64.slice(b64.indexOf(',') + 1) : b64;
  const bin = atob(clean);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function extForMime(mime: string): string {
  if (mime.includes('png')) return 'png';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('heic') || mime.includes('heif')) return 'heic';
  return 'jpg';
}

/** Carica UNA pagina su staging; ritorna il path `<tenant>/<uuid>.<ext>`. */
export async function uploadPageToStaging(
  tenantId: string,
  b64: string,
  mime: string,
): Promise<string> {
  const ext = extForMime(mime);
  const path = `${tenantId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await getSupabase()
    .storage.from(STAGING_BUCKET)
    .upload(path, b64ToBytes(b64), { contentType: mime, upsert: false });
  if (error) throw new Error(`staging_upload_failed: ${error.message}`);
  return path;
}

/** Enqueue idempotente (su client_draft_uuid). Ritorna il job_id. */
export async function enqueueDocumentJob(args: {
  businessId: string;
  pagePaths: string[];
  docKind: 'ddt' | 'fattura' | null;
  clientDraftUuid: string;
}): Promise<string> {
  const { data, error } = await getSupabase().rpc('enqueue_document_job', {
    p_business_id: args.businessId,
    p_page_paths: args.pagePaths,
    p_doc_kind: args.docKind,
    p_client_draft_uuid: args.clientDraftUuid,
  });
  if (error) throw new Error(`enqueue_failed: ${error.message}`);
  return data as string;
}

/** Poll dei job del tenant/business (più recenti). Zod-parse, scarta righe invalide. */
export async function fetchDocumentJobs(
  tenantId: string,
  businessId: string,
  limit = 30,
): Promise<DocumentJobRow[]> {
  const { data, error } = await getSupabase()
    .from('document_jobs')
    .select(
      'id,tenant_id,business_id,client_draft_uuid,status,step,page_paths,doc_kind_guess,result,duplicate,error,attempts,committed_document_id,created_at,updated_at',
    )
    .eq('tenant_id', tenantId)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`fetch_jobs_failed: ${error.message}`);
  const rows: DocumentJobRow[] = [];
  for (const r of data ?? []) {
    const parsed = DocumentJobRowSchema.safeParse(r);
    if (parsed.success) rows.push(parsed.data);
  }
  return rows;
}

/** Carica un singolo job per id (review-from-job). */
export async function fetchDocumentJob(jobId: string): Promise<DocumentJobRow | null> {
  const { data, error } = await getSupabase()
    .from('document_jobs')
    .select(
      'id,tenant_id,business_id,client_draft_uuid,status,step,page_paths,doc_kind_guess,result,duplicate,error,attempts,committed_document_id,created_at,updated_at',
    )
    .eq('id', jobId)
    .maybeSingle();
  if (error || !data) return null;
  const parsed = DocumentJobRowSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

/**
 * Firma le pagine staged di un job per la visualizzazione (T-335).
 * Anche quando il job è "pronto" l'utente deve poter rivedere la foto/PDF
 * caricato senza avere il documento cartaceo davanti. Bucket privato → signed
 * URL a 15 min. Ritorna `{ path, url, isPdf }[]` nell'ordine delle pagine;
 * scarta le pagine non firmabili invece di far fallire tutto.
 */
export async function signStagingPages(
  pagePaths: string[],
): Promise<{ path: string; url: string; isPdf: boolean }[]> {
  const out: { path: string; url: string; isPdf: boolean }[] = [];
  for (const path of pagePaths) {
    const { data, error } = await getSupabase()
      .storage.from(STAGING_BUCKET)
      .createSignedUrl(path, 900);
    if (error || !data?.signedUrl) continue;
    out.push({ path, url: data.signedUrl, isPdf: path.toLowerCase().endsWith('.pdf') });
  }
  return out;
}

/** Marca un job come committed (dopo che la review è stata confermata). */
export async function markJobCommitted(jobId: string, documentId?: string | null): Promise<void> {
  await getSupabase()
    .from('document_jobs')
    .update({ status: 'committed', step: 'committed', committed_document_id: documentId ?? null })
    .eq('id', jobId);
}

/** Annulla (dismiss) un job dalla lista "In elaborazione". */
export async function dismissDocumentJob(jobId: string): Promise<void> {
  const { error } = await getSupabase()
    .from('document_jobs')
    .update({ status: 'dismissed', step: 'dismissed' })
    .eq('id', jobId);
  if (error) throw new Error(`dismiss_failed: ${error.message}`);
}

/** Rimette in coda un job fallito (Riprova). */
export async function retryDocumentJob(jobId: string): Promise<void> {
  const { error } = await getSupabase()
    .from('document_jobs')
    .update({ status: 'queued', step: 'queued', attempts: 0, error: null, claimed_at: null })
    .eq('id', jobId);
  if (error) throw new Error(`retry_failed: ${error.message}`);
}
